import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminSidebar from '../../components/ui/AdminSidebar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import contactService from '../../utils/contactService';

const AdminContactManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({});
  const [adminUsers, setAdminUsers] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showContactDetail, setShowContactDetail] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    issueType: '',
    assignedTo: '',
    search: ''
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalContacts, setTotalContacts] = useState(0);
  const contactsPerPage = 20;

  useEffect(() => {
    loadContacts();
    loadStats();
    loadAdminUsers();
    
    // Check if there's a specific contact to show from URL params
    const contactId = searchParams.get('contact');
    if (contactId) {
      loadContactDetail(contactId);
    }
  }, [filters, currentPage, searchParams]);

  const loadContacts = async () => {
    setLoading(true);
    try {
      const offset = (currentPage - 1) * contactsPerPage;
      const result = await contactService.getAllContacts(filters, contactsPerPage, offset);
      
      if (result.success) {
        setContacts(result.data);
        setTotalContacts(result.data.length); // This would need to be updated if you implement proper pagination
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const result = await contactService.getContactStats();
      if (result.success) {
        setStats(result.data);
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const loadAdminUsers = async () => {
    try {
      const result = await contactService.getAdminUsers();
      if (result.success) {
        setAdminUsers(result.data);
      }
    } catch (err) {
      console.error('Failed to load admin users:', err);
    }
  };

  const loadContactDetail = async (contactId) => {
    try {
      const result = await contactService.getContactById(contactId);
      if (result.success) {
        setSelectedContact(result.data);
        setShowContactDetail(true);
      }
    } catch (err) {
      console.error('Failed to load contact detail:', err);
    }
  };

  const handleStatusUpdate = async (contactId, newStatus, adminNote = null) => {
    try {
      const result = await contactService.updateContactStatusWithEmail(contactId, newStatus, adminNote);
      if (result.success) {
        loadContacts();
        loadStats(); // Refresh stats to show updated counts
        if (selectedContact && selectedContact.id === contactId) {
          loadContactDetail(contactId);
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to update contact status');
    }
  };

  const handleAssignContact = async (contactId, adminUserId) => {
    try {
      const result = await contactService.assignContact(contactId, adminUserId);
      if (result.success) {
        loadContacts();
        if (selectedContact && selectedContact.id === contactId) {
          loadContactDetail(contactId);
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to assign contact');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      priority: '',
      issueType: '',
      assignedTo: '',
      search: ''
    });
    setCurrentPage(1);
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      'open': 'bg-red-100 text-red-800',
      'in_progress': 'bg-yellow-100 text-yellow-800',
      'resolved': 'bg-green-100 text-green-800',
      'closed': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityBadgeColor = (priority) => {
    const colors = {
      'low': 'bg-gray-100 text-gray-800',
      'normal': 'bg-blue-100 text-blue-800',
      'high': 'bg-orange-100 text-orange-800',
      'urgent': 'bg-red-100 text-red-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  if (loading && contacts.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <AdminSidebar />
        <div className="lg:pl-72">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Icon name="Loader2" size={32} className="animate-spin mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">Loading contacts...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminSidebar />
      
      <div className="lg:pl-72">
        <div className="p-6">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Contact Management</h1>
                <p className="text-muted-foreground mt-2">Manage user inquiries and support tickets</p>
              </div>
              <Button
                variant="outline"
                iconName="RefreshCw"
                onClick={loadContacts}
                disabled={loading}
              >
                Refresh
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Contacts</p>
                  <p className="text-2xl font-bold text-foreground">{stats.total_contacts || 0}</p>
                </div>
                <Icon name="MessageSquare" size={24} className="text-muted-foreground" />
              </div>
            </div>
            
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Open Tickets</p>
                  <p className="text-2xl font-bold text-red-600">{stats.open_contacts || 0}</p>
                </div>
                <Icon name="AlertCircle" size={24} className="text-red-600" />
              </div>
            </div>
            
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">In Progress</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.in_progress_contacts || 0}</p>
                </div>
                <Icon name="Clock" size={24} className="text-yellow-600" />
              </div>
            </div>
            
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Resolved Today</p>
                  <p className="text-2xl font-bold text-green-600">{stats.resolved_today || 0}</p>
                </div>
                <Icon name="CheckCircle" size={24} className="text-green-600" />
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-card rounded-lg border border-border p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Filters</h3>
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear All
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <Input
                placeholder="Search contacts..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                iconName="Search"
              />
              
              <Select
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value)}
                placeholder="All Statuses"
                options={[
                  { value: '', label: 'All Statuses' },
                  { value: 'open', label: 'Open' },
                  { value: 'in_progress', label: 'In Progress' },
                  { value: 'resolved', label: 'Resolved' },
                  { value: 'closed', label: 'Closed' }
                ]}
              />
              
              <Select
                value={filters.priority}
                onChange={(value) => handleFilterChange('priority', value)}
                placeholder="All Priorities"
                options={[
                  { value: '', label: 'All Priorities' },
                  { value: 'low', label: 'Low' },
                  { value: 'normal', label: 'Normal' },
                  { value: 'high', label: 'High' },
                  { value: 'urgent', label: 'Urgent' }
                ]}
              />
              
              <Select
                value={filters.issueType}
                onChange={(value) => handleFilterChange('issueType', value)}
                placeholder="All Issue Types"
                options={[
                  { value: '', label: 'All Issue Types' },
                  ...contactService.getIssueTypeOptions()
                ]}
              />
              
              <Select
                value={filters.assignedTo}
                onChange={(value) => handleFilterChange('assignedTo', value)}
                placeholder="All Assignees"
                options={[
                  { value: '', label: 'All Assignees' },
                  { value: 'unassigned', label: 'Unassigned' },
                  ...adminUsers.map(admin => ({
                    value: admin.id,
                    label: admin.full_name || admin.email
                  }))
                ]}
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <Icon name="AlertCircle" size={20} className="text-red-600 mr-2" />
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* Contacts Table */}
          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Issue Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Assigned To
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {contacts.map((contact) => (
                    <tr key={contact.id} className="hover:bg-muted/30">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-foreground">
                            {contact.subject}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {contact.user_profiles?.full_name || contact.user_profiles?.email || 'Anonymous'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-foreground">
                          {contactService.getIssueTypeLabel(contact.issue_type)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(contact.status)}`}>
                          {contactService.getStatusLabel(contact.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityBadgeColor(contact.priority)}`}>
                          {contactService.getPriorityLabel(contact.priority)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-foreground">
                          {contact.assigned_admin?.full_name || contact.assigned_admin?.email || 'Unassigned'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-muted-foreground">
                          {contactService.formatContactTime(contact.created_at)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Eye"
                            onClick={() => {
                              setSelectedContact(contact);
                              setShowContactDetail(true);
                            }}
                          />
                          <Select
                            value={contact.status}
                            onChange={(value) => handleStatusUpdate(contact.id, value)}
                            options={[
                              { value: 'open', label: 'Open' },
                              { value: 'in_progress', label: 'In Progress' },
                              { value: 'resolved', label: 'Resolved' },
                              { value: 'closed', label: 'Closed' }
                            ]}
                            className="w-32"
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {contacts.length === 0 && !loading && (
              <div className="text-center py-12">
                <Icon name="MessageSquare" size={48} className="mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No contacts found</h3>
                <p className="text-muted-foreground">
                  {Object.values(filters).some(f => f) ? 'Try adjusting your filters' : 'No contacts have been submitted yet'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Contact Detail Modal */}
      {showContactDetail && selectedContact && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-foreground">Contact Details</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  onClick={() => setShowContactDetail(false)}
                />
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">{selectedContact.subject}</h3>
                  <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                    <span>From: {selectedContact.user_profiles?.full_name || selectedContact.user_profiles?.email || 'Anonymous'}</span>
                    <span>•</span>
                    <span>{contactService.formatContactTime(selectedContact.created_at)}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Issue Type</label>
                    <p className="text-foreground">{contactService.getIssueTypeLabel(selectedContact.issue_type)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Priority</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityBadgeColor(selectedContact.priority)}`}>
                      {contactService.getPriorityLabel(selectedContact.priority)}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Message</label>
                  <div className="mt-1 p-3 bg-muted/30 rounded-lg">
                    <p className="text-foreground whitespace-pre-wrap">{selectedContact.message}</p>
                  </div>
                </div>

                {selectedContact.source_page && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Source Page</label>
                    <p className="text-foreground">{selectedContact.source_page}</p>
                    {selectedContact.source_url && (
                      <p className="text-sm text-muted-foreground">{selectedContact.source_url}</p>
                    )}
                  </div>
                )}

                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <label className="text-sm font-medium text-muted-foreground">Assign to Admin</label>
                    <Select
                      value={selectedContact.assigned_to || ''}
                      onChange={(value) => handleAssignContact(selectedContact.id, value)}
                      placeholder="Select admin..."
                      options={[
                        { value: '', label: 'Unassigned' },
                        ...adminUsers.map(admin => ({
                          value: admin.id,
                          label: admin.full_name || admin.email
                        }))
                      ]}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-sm font-medium text-muted-foreground">Status</label>
                    <Select
                      value={selectedContact.status}
                      onChange={(value) => handleStatusUpdate(selectedContact.id, value)}
                      options={[
                        { value: 'open', label: 'Open' },
                        { value: 'in_progress', label: 'In Progress' },
                        { value: 'resolved', label: 'Resolved' },
                        { value: 'closed', label: 'Closed' }
                      ]}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContactManagement;
